import store from '../../state';

export interface AboutLayerItem {
  id: string;
  title: string;
  icon: string;
  badge: string;
  description: string;
  imageSrc?: string;
}

export const ABOUT_LAYERS: AboutLayerItem[] = [
  {
    id: 'dark_skies',
    title: 'Dark Skies & Astrophotography',
    icon: '✨',
    badge: 'Stargazing & Optics',
    description: 'An hour-by-hour forecast showing project night sky viewing conditions.  Combines high-resolution artificial light pollution baselines (VIIRS/Bortle scale) with dynamic weather, solar and lunar data to reveal where conditions will be good for star gazing before you head out.',
    imageSrc: 'assets/about/dark_skies.png'
  },
  {
    id: 'sunrise_sunset',
    title: 'Sunrise & Sunset Quality Scores',
    icon: '🌅',
    badge: 'Landscape Photography',
    description: 'Provides daily sunrise and sunset color quality using a host of surface and atmospheric inputs from multiple weather forecast models.',
    imageSrc: 'assets/about/sunrise_sunset.png'
  },
  {
    id: 'alpenglow',
    title: 'Alpenglow Peak Illumination',
    icon: '🏔️',
    badge: 'Alpine Lighting',
    description: 'Shows projected likliehood and quality of alpenglow (and an excellent golden hour light quality proxy) through specialized 3D terrain line-of-sight modeling that identifies when high mountain peaks and dramatic ridgelines will catch unblocked direct and indirect twilight illumination during dawn and dusk, casting warm pink and magenta hues.',
    imageSrc: 'assets/about/alpenglow.png'
  },
  {
    id: 'valley_fog',
    title: 'Valley Fog & Inversions',
    icon: '🌫️',
    badge: 'Lowland Microclimates',
    description: 'Projects where conditions are ripe for low lying and valley fog development based on various atmospheric conditions to help you differntiate between low clouds and fog layers.',
    imageSrc: 'assets/about/valley_fog.png'
  },
  {
    id: 'lenticular',
    title: 'Lenticular Cloud Probability',
    icon: '🛸',
    badge: 'Atmospheric Physics',
    description: 'Detects standing atmospheric gravity waves and mountain wave moisture traps. Analyzes mid-tropospheric wind shear, stability indices, and terrain-perpendicular airflow to pinpoint where smooth, saucer-shaped lenticular clouds will form.',
    imageSrc: 'assets/about/lenticular.png'
  },
  {
    id: 'fall_foliage',
    title: 'Fall Foliage Autumn Colors',
    icon: '🍂',
    badge: 'Phenology & Ecology',
    description: 'Multi-stage phenological modeling tracking cumulative cooling degree days, photoperiod shifts, soil moisture stress, and elevation gradients to map autumn leaf color transitions from early patchiness to vibrant peak color.',
    imageSrc: 'assets/about/fall_foliage.png'
  },
  {
    id: 'solar_lunar',
    title: 'Solar & Lunar Ephemeris Tools',
    icon: '☀️',
    badge: 'Sun & Moon Tracking',
    description: 'Precision astronomical calculators projecting interactive sun and moon trajectories, exact golden hour and blue hour windows, astronomical twilight, moonrise/moonset times, lunar phase/illumination, and real-time azimuth and elevation angles.',
    imageSrc: 'assets/about/solar_lunar.png'
  },
  {
    id: 'optics_dew',
    title: 'Optics Dew & Condensation Risk',
    icon: '🔭',
    badge: 'Equipment Diagnostics',
    description: 'High-precision condensation diagnostics tailored for astrophotographers, telescope observers, and camera operators. Calculates lens and mirror temperature deficits against ambient dew points to warn of lens fogging in advance.',
    imageSrc: 'assets/about/optics_dew.png'
  },
  {
    id: 'smoke_aqi',
    title: 'Wildfire Smoke & AQI',
    icon: '💨',
    badge: 'Air Quality & Health',
    description: 'Integrates HRRR-Smoke surface and vertically integrated smoke dispersion with EPA AirNow PM2.5 and Ozone AQI monitoring stations, giving outdoor enthusiasts and athletes clear foresight into breathable air windows and smoke plumes.',
    imageSrc: 'assets/about/smoke_aqi.png'
  },
  {
    id: 'snow_depth',
    title: 'Snow Depth & Snowpack',
    icon: '❄️',
    badge: 'Winter & Alpine',
    description: 'Combines NOHRSC snow analysis and high-resolution atmospheric models to visualize snow water equivalent (SWE), total snowpack depth, freezing levels, and recent accumulation across mountain ranges and backcountry terrain.',
    imageSrc: 'assets/about/snow_depth.png'
  },
  {
    id: 'streamflow',
    title: 'Streams, Rivers & Reservoirs',
    icon: '🌊',
    badge: 'Hydrology & Watersports',
    description: 'Real-time USGS and Environment Canada hydrologic telemetry mapped with streamflow gauges (discharge in CFS and percentiles), river water temperatures, and reservoir water level tracking for paddlers, anglers, and water sports.',
    imageSrc: 'assets/about/streamflow.png'
  }
];

export class AboutModal {
  private container: HTMLElement;
  private onStartTour?: () => void;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(onStartTour?: () => void) {
    this.onStartTour = onStartTour;
    this.container = document.createElement('div');
    this.container.id = 'gw-about-modal';
    this.container.className = 'gw-about-modal-backdrop';
    this.container.style.display = 'none';

    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  public close(): void {
    this.container.style.display = 'none';
    this.container.innerHTML = '';
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
  }

  private escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  public open(): void {
    this.close();
    this.container.style.display = 'flex';

    const state = store.getState();
    const isStealth = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.stealth_mode) ?? true;
    const isEntitled = state.isAuthenticated && state.subscription?.hasAccess;

    let stealthBannerHtml = '';
    if (isStealth && !isEntitled) {
      if (!state.isAuthenticated) {
        stealthBannerHtml = `
          <div class="gw-about-stealth-banner">
            <div class="gw-about-stealth-banner-left">
              <span class="gw-about-stealth-icon">🔒</span>
              <div>
                <strong class="gw-about-stealth-title">Private Beta Preview</strong>
                <p class="gw-about-stealth-desc">SkySkout is currently operating in an invite-only stealth preview. Sign in or register to request access to the forecast layer catalog.</p>
              </div>
            </div>
            <button id="gw-about-auth-cta-btn" class="gw-about-auth-cta-btn">
              <span>🔑 Sign In / Register</span>
            </button>
          </div>
        `;
      } else {
        const email = state.user?.email || 'User';
        const uid = state.user?.uid || '';
        stealthBannerHtml = `
          <div class="gw-about-stealth-banner pending">
            <div class="gw-about-stealth-banner-left">
              <span class="gw-about-stealth-icon">⏳</span>
              <div>
                <strong class="gw-about-stealth-title">Account Pending Administrator Activation</strong>
                <p class="gw-about-stealth-desc">Signed in as <strong>${this.escapeHtml(email)}</strong>. Provide your UID (<code style="color: #fef08a;">${this.escapeHtml(uid)}</code>) to an administrator to activate your access.</p>
              </div>
            </div>
          </div>
        `;
      }
    }

    const cardsHtml = ABOUT_LAYERS.map(layer => {
      return `
        <div class="gw-about-layer-card" data-layer-id="${layer.id}">
          <div class="gw-about-thumb-container">
            <img
              src="${layer.imageSrc || ''}"
              alt="${layer.title} Screenshot"
              class="gw-about-thumb-img"
              width="200"
              height="200"
              onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
            />
            <div class="gw-about-thumb-placeholder" style="display: none;">
              <span class="gw-about-ph-icon">${layer.icon}</span>
              <span class="gw-about-ph-label">200 × 200</span>
              <span class="gw-about-ph-sub">${layer.badge}</span>
            </div>
          </div>
          <div class="gw-about-card-content">
            <div class="gw-about-card-header">
              <span class="gw-about-card-icon">${layer.icon}</span>
              <div>
                <h3 class="gw-about-card-title">${layer.title}</h3>
                <span class="gw-about-card-badge">${layer.badge}</span>
              </div>
            </div>
            <p class="gw-about-card-desc">${layer.description}</p>
          </div>
        </div>
      `;
    }).join('');

    this.container.innerHTML = `
      <div class="gw-about-dialog" role="dialog" aria-modal="true" aria-labelledby="gw-about-modal-title">
        <div class="gw-about-header">
          <div class="gw-about-title-group">
            <span class="gw-about-logo-icon">🌤️</span>
            <div>
              <h2 id="gw-about-modal-title" class="gw-about-title">About SkySkout</h2>
              <span class="gw-about-subtitle">High-Resolution Outdoor & Specialized Meteorological Forecasting by CascadiaSoft LLC</span>
            </div>
          </div>
          <button id="gw-about-close-btn" class="gw-about-close" title="Close" aria-label="Close">&times;</button>
        </div>

        <div class="gw-about-body">
          ${stealthBannerHtml}

          <!-- Top Feature Tour Banner -->
          <div class="gw-about-tour-banner">
            <div class="gw-about-tour-banner-left">
              <span class="gw-about-tour-icon">🧭</span>
              <div>
                <strong class="gw-about-tour-title">New to SkySkout?</strong>
                <p class="gw-about-tour-desc">Take the interactive walkthrough to learn how to navigate weather layers, timelines, and scout forecasts.</p>
              </div>
            </div>
            <button id="gw-about-start-tour-btn" class="gw-about-tour-btn">
              <span>🚀 Quick Feature Tour</span>
            </button>
          </div>

          <!-- Value Proposition Intro -->
          <div class="gw-about-intro-section">
            <h3 class="gw-about-section-heading">Key Value Propositions</h3>
            <p class="gw-about-intro-text">
              SkySkout blends multi-model ensemble weather data (HRRR, GFS, NAM, ECMWF) with custom atmospheric physics and high-resolution terrain elevation models. Designed specifically for outdoor enthusiasts, landscape photographers, astrophotographers, hikers, backcountry skiers, and water sports participants.
            </p>
          </div>

          <!-- Specialized Layers Grid -->
          <div class="gw-about-layers-section">
            <h3 class="gw-about-section-heading">Proprietary & Specialized Weather Layers</h3>
            <div class="gw-about-layers-grid">
              ${cardsHtml}
            </div>
          </div>
        </div>

        <div class="gw-about-footer">
          <button id="gw-about-ok-btn" class="gw-about-ok-btn">Close</button>
        </div>
      </div>
    `;

    // Hook events
    const closeBtn = document.getElementById('gw-about-close-btn');
    if (closeBtn) closeBtn.onclick = () => this.close();

    const okBtn = document.getElementById('gw-about-ok-btn');
    if (okBtn) okBtn.onclick = () => this.close();

    const authCtaBtn = document.getElementById('gw-about-auth-cta-btn');
    if (authCtaBtn) {
      authCtaBtn.onclick = () => {
        this.close();
        (window as any).__authModal?.open('signin');
      };
    }

    const tourBtn = document.getElementById('gw-about-start-tour-btn');
    if (tourBtn) {
      tourBtn.onclick = () => {
        this.close();
        if (this.onStartTour) {
          this.onStartTour();
        }
      };
    }

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
    };
    window.addEventListener('keydown', this.keydownHandler);
  }
}

export default AboutModal;
